import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import * as Icons from "lucide-react";
import { CheckCircle, ArrowRight } from "lucide-react";

export default function ServicesPage() {
  const { t } = useLanguage();
  const categories = t("services.categories");
  const diseases = t("diseases.items");
  const diagnosticItems = t("diagnostics.items");
  const treatmentItems = t("treatments.items");
  const prevention = t("prevention");

  return (
    <div className="min-h-screen pt-20">
      {/* Page header */}
      <section className="py-20 bg-[#F4F1EA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center space-y-4">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4A6753]">{t("services.overline")}</span>
          <h1 data-testid="services-page-title" className="font-heading text-4xl sm:text-5xl tracking-tight text-[#1E2922] font-semibold">
            {t("services.title")}
          </h1>
          <p className="text-base leading-relaxed text-[#566D60] max-w-2xl mx-auto">{t("services.subtitle")}</p>
        </div>
      </section>

      {/* Service categories grid */}
      <section className="py-16 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Array.isArray(categories) && categories.map((cat, i) => {
              const IconComponent = Icons[cat.icon] || Icons.Activity;
              const isExternal = cat.link.startsWith("/home") || cat.link.startsWith("/life");
              return (
                <Link key={i} to={isExternal ? cat.link : `#${cat.link.split('#')[1] || ''}`}
                  onClick={!isExternal ? (e) => {
                    e.preventDefault();
                    const id = cat.link.split('#')[1];
                    if (id) document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
                  } : undefined}
                  data-testid={`service-category-${i}`}
                  className="bg-[#F4F1EA] rounded-2xl p-8 border border-transparent hover:border-[#E2E0D9] transition-all duration-300 hover:-translate-y-1 shadow-sm hover:shadow-md block group">
                  <div className="w-14 h-14 rounded-xl bg-[#4A6753]/10 flex items-center justify-center mb-6 group-hover:bg-[#4A6753]/20 transition-colors">
                    <IconComponent size={28} className="text-[#4A6753]" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-medium text-[#1E2922] mb-3 font-heading">{cat.title}</h3>
                  <p className="text-sm leading-relaxed text-[#566D60]">{cat.description}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Diseases Section */}
      <section id="diseases" className="py-24 bg-[#F4F1EA]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <h2 data-testid="diseases-section-title" className="font-heading text-3xl sm:text-4xl tracking-tight text-[#1E2922] mb-8">
            {t("diseases.title")}
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {Array.isArray(diseases) && diseases.map((disease, i) => (
              <AccordionItem key={i} value={`disease-${i}`} className="bg-white rounded-xl border border-[#E2E0D9] px-6 overflow-hidden">
                <AccordionTrigger data-testid={`disease-accordion-${i}`} className="text-left font-heading text-lg font-medium text-[#1E2922] hover:no-underline py-5">
                  {disease.title}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-[#566D60] pb-5">
                  {disease.content}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Diagnostics Section */}
      <section id="diagnostics" className="py-24 bg-[#FDFBF7]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <h2 data-testid="diagnostics-section-title" className="font-heading text-3xl sm:text-4xl tracking-tight text-[#1E2922] mb-8">
            {t("diagnostics.title")}
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {Array.isArray(diagnosticItems) && diagnosticItems.map((item, i) => (
              <AccordionItem key={i} value={`diag-${i}`} className="bg-[#F4F1EA] rounded-xl border border-[#E2E0D9] px-6 overflow-hidden">
                <AccordionTrigger data-testid={`diagnostic-accordion-${i}`} className="text-left font-heading text-lg font-medium text-[#1E2922] hover:no-underline py-5">
                  {item.title}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-[#566D60] pb-5">
                  {item.content}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Treatments Section */}
      <section id="treatments" className="py-24 bg-[#F4F1EA]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <h2 data-testid="treatments-section-title" className="font-heading text-3xl sm:text-4xl tracking-tight text-[#1E2922] mb-8">
            {t("treatments.title")}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Array.isArray(treatmentItems) && treatmentItems.map((item, i) => (
              <div key={i} data-testid={`treatment-item-${i}`} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-[#E2E0D9]">
                <CheckCircle size={16} className="text-[#4A6753] mt-0.5 shrink-0" />
                <span className="text-sm text-[#566D60] leading-relaxed">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Prevention Section */}
      <section id="prevention" className="py-24 bg-[#FDFBF7]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 space-y-8">
          <h2 data-testid="prevention-section-title" className="font-heading text-3xl sm:text-4xl tracking-tight text-[#1E2922]">
            {typeof prevention === 'object' ? prevention.title : ''}
          </h2>
          <p className="text-base leading-relaxed text-[#566D60]">
            {typeof prevention === 'object' ? prevention.description : ''}
          </p>
          <div className="bg-[#F4F1EA] rounded-2xl p-8 space-y-6">
            <h3 className="font-heading text-xl font-medium text-[#1E2922]">
              {typeof prevention === 'object' ? prevention.healthy_life_title : ''}
            </h3>
            <p className="text-sm text-[#566D60] italic">
              {typeof prevention === 'object' ? prevention.healthy_life_intro : ''}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {typeof prevention === 'object' && Array.isArray(prevention.healthy_life_items) && prevention.healthy_life_items.map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-[#4A6753] mt-0.5 shrink-0" />
                  <span className="text-sm text-[#566D60]">{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="text-center pt-4">
            <Link to="/contact" data-testid="prevention-contact-link" className="inline-flex items-center gap-2 bg-[#4A6753] text-white rounded-full px-8 py-3.5 font-medium hover:bg-[#3A5242] transition-colors shadow-sm">
              {t("hero.cta")} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
